export interface CSVColumn {
	key: string;
	header: string;
	formatter?: (value: any) => string;
}

export class CSVExporter {
	/**
	 * Generate CSV content from data array
	 */
	static generateCSV(data: any[], columns: CSVColumn[]): string {
		// Create header row
		const headers = columns.map(col => col.header).join(',');
		
		// Create data rows
		const rows = data.map(row => {
			return columns.map(col => {
				const value = row[col.key];
				const formattedValue = col.formatter ? col.formatter(value) : value;
				
				// Escape commas and quotes for CSV format
				const escaped = String(formattedValue || '')
					.replace(/"/g, '""');
				
				return `"${escaped}"`;
			}).join(',');
		});
		
		return [headers, ...rows].join('\n');
	}
	
	/**
	 * Trigger browser download of CSV file
	 */
	static downloadCSV(csvContent: string, filename: string): void {
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		
		if (link.download !== undefined) {
			const url = URL.createObjectURL(blob);
			link.setAttribute('href', url);
			link.setAttribute('download', filename);
			link.style.visibility = 'hidden';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		}
	}
	
	/**
	 * Export complete report data to CSV
	 */
	static exportReportData(reportData: any, shopName: string, dateRange: string): void {
		const timestamp = new Date().toISOString().split('T')[0];
		const filename = `${shopName}_Report_${dateRange}_${timestamp}.csv`;
		
		const sections: string[] = [];
		
		// Summary section
		sections.push('SUMMARY METRICS');
		sections.push('Metric,Value');
		sections.push(`Total Sales,₹${reportData.totalSales || 0}`);
		sections.push(`Service Sales,₹${reportData.totalServiceSales || 0}`);
		sections.push(`Product Sales,₹${reportData.totalProductSales || 0}`);
		sections.push(`Total Customers,${reportData.totalCustomers || 0}`);
		sections.push(`New Customers,${reportData.totalNewCustomers || 0}`);
		sections.push(`Existing Customers,${reportData.totalExistingCustomers || 0}`);
		sections.push('');
		
		// Payment breakdown
		if (reportData.paymentReport) {
			sections.push('PAYMENT BREAKDOWN');
			sections.push('Method,Amount');
			sections.push(`Cash,₹${reportData.paymentReport.cash || 0}`);
			sections.push(`Card,₹${reportData.paymentReport.card || 0}`);
			sections.push(`UPI,₹${reportData.paymentReport.upi || 0}`);
			sections.push('');
		}
		
		// Daily sales (if available)
		if (reportData.dailySales && reportData.dailySales.length > 0) {
			sections.push('DAILY SALES');
			sections.push('Date,Sales,Label');
			reportData.dailySales.forEach((day: any) => {
				sections.push(`${day.date},₹${day.value},${day.label}`);
			});
			sections.push('');
		}
		
		// Staff performance (if available)
		if (reportData.staffPerformance && reportData.staffPerformance.length > 0) {
			sections.push('STAFF PERFORMANCE');
			sections.push('Staff Name,Total Revenue,Service Revenue,Product Revenue,Order Count,Average Order Value');
			reportData.staffPerformance.forEach((staff: any) => {
				sections.push(
					`${staff.userName},₹${staff.totalRevenue},₹${staff.serviceRevenue},₹${staff.productRevenue},${staff.orderCount},₹${staff.averageOrderValue}`
				);
			});
			sections.push('');
		}
		
		// Service categories (if available)
		if (reportData.serviceCategories && reportData.serviceCategories.length > 0) {
			sections.push('SERVICE REVENUE BY CATEGORY');
			sections.push('Service Name,Revenue,Percentage,Count');
			reportData.serviceCategories.forEach((cat: any) => {
				sections.push(`${cat.categoryName},₹${cat.revenue},${cat.percentage}%,${cat.count}`);
			});
			sections.push('');
		}
		
		// Product categories (if available)
		if (reportData.productCategories && reportData.productCategories.length > 0) {
			sections.push('PRODUCT REVENUE BY CATEGORY');
			sections.push('Product Name,Revenue,Percentage,Count');
			reportData.productCategories.forEach((cat: any) => {
				sections.push(`${cat.categoryName},₹${cat.revenue},${cat.percentage}%,${cat.count}`);
			});
			sections.push('');
		}
		
		// Inventory analysis (if available)
		if (reportData.inventoryAnalysis && reportData.inventoryAnalysis.length > 0) {
			sections.push('INVENTORY ANALYSIS');
			sections.push('Product Name,Current Stock,Reserved,Available,Usage,Status,Days Until Stockout');
			reportData.inventoryAnalysis.forEach((item: any) => {
				sections.push(
					`${item.productName},${item.currentStock},${item.quantityReserved},${item.quantityAvailable},${item.usageInPeriod},${item.status},${item.daysUntilStockout || 'N/A'}`
				);
			});
			sections.push('');
		}
		
		// Orders (if available)
		if (reportData.orders && reportData.orders.length > 0) {
			sections.push('ORDERS');
			sections.push('Order ID,Customer,Total,Payment Method,Date');
			reportData.orders.forEach((order: any) => {
				const date = new Date(order.createdAt).toLocaleDateString();
				sections.push(
					`${order.sequenceNumber},${order.customerName || 'N/A'},₹${order.total},${order.paymentMethod},${date}`
				);
			});
		}
		
		const csvContent = sections.join('\n');
		this.downloadCSV(csvContent, filename);
	}
	
	/**
	 * Export orders only
	 */
	static exportOrders(orders: any[], shopName: string): void {
		const timestamp = new Date().toISOString().split('T')[0];
		const filename = `${shopName}_Orders_${timestamp}.csv`;
		
		const columns: CSVColumn[] = [
			{ key: 'sequenceNumber', header: 'Order ID' },
			{ key: 'customerName', header: 'Customer' },
			{ key: 'total', header: 'Total', formatter: (val) => `₹${val}` },
			{ key: 'paymentMethod', header: 'Payment Method' },
			{ key: 'createdAt', header: 'Date', formatter: (val) => new Date(val).toLocaleDateString() }
		];
		
		const csvContent = this.generateCSV(orders, columns);
		this.downloadCSV(csvContent, filename);
	}
	
	/**
	 * Export staff performance only
	 */
	static exportStaffPerformance(staffData: any[], shopName: string): void {
		const timestamp = new Date().toISOString().split('T')[0];
		const filename = `${shopName}_StaffPerformance_${timestamp}.csv`;
		
		const columns: CSVColumn[] = [
			{ key: 'userName', header: 'Staff Name' },
			{ key: 'totalRevenue', header: 'Total Revenue', formatter: (val) => `₹${val}` },
			{ key: 'serviceRevenue', header: 'Service Revenue', formatter: (val) => `₹${val}` },
			{ key: 'productRevenue', header: 'Product Revenue', formatter: (val) => `₹${val}` },
			{ key: 'orderCount', header: 'Order Count' },
			{ key: 'averageOrderValue', header: 'Avg Order Value', formatter: (val) => `₹${val}` }
		];
		
		const csvContent = this.generateCSV(staffData, columns);
		this.downloadCSV(csvContent, filename);
	}
	
	/**
	 * Export inventory analysis only
	 */
	static exportInventoryAnalysis(inventoryData: any[], shopName: string): void {
		const timestamp = new Date().toISOString().split('T')[0];
		const filename = `${shopName}_InventoryAnalysis_${timestamp}.csv`;
		
		const columns: CSVColumn[] = [
			{ key: 'productName', header: 'Product Name' },
			{ key: 'currentStock', header: 'Current Stock' },
			{ key: 'quantityReserved', header: 'Reserved' },
			{ key: 'quantityAvailable', header: 'Available' },
			{ key: 'usageInPeriod', header: 'Usage in Period' },
			{ key: 'status', header: 'Status' },
			{ key: 'daysUntilStockout', header: 'Days Until Stockout', formatter: (val) => val || 'N/A' }
		];
		
		const csvContent = this.generateCSV(inventoryData, columns);
		this.downloadCSV(csvContent, filename);
	}
}
